/**
 * Single tab session: ensures only one browser tab can use the application
 * (and thus the auth token in localStorage) at a time.
 * Uses BroadcastChannel API for cross-tab communication.
 */

const CHANNEL_NAME = 'nemipay-dashboard-session';
const HEARTBEAT_INTERVAL_MS = 2000;
const ACQUIRE_TIMEOUT_MS = 600;

export type SessionMessage =
  | { type: 'WHO_IS_OWNER' }
  | { type: 'I_AM_OWNER'; tabId: string }
  | { type: 'HEARTBEAT'; tabId: string }
  | { type: 'RELEASED'; tabId: string };

let channel: BroadcastChannel | null = null;
let tabId = '';
let isOwner = false;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

function getTabId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function startHeartbeat(): void {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(() => {
    if (channel) {
      try {
        channel.postMessage({ type: 'HEARTBEAT', tabId, } as SessionMessage);
      } catch {
        // Channel may already be closed or unavailable; ignore to avoid breaking the heartbeat loop
      }
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function releaseSession(): void {
  if (channel && isOwner) {
    try {
      channel.postMessage({ type: 'RELEASED', tabId, } as SessionMessage);
    } catch {
      // Channel may already be closed
    }
  }
  stopHeartbeat();
  if (channel) {
    channel.close();
    channel = null;
  }
  isOwner = false;
}

/**
 * Initialize single-tab session.
 * @returns Promise<boolean> - true if this tab has the session (can use token),
 *   false if another tab already has it (should block token usage).
 */
export function initSingleTabSession(): Promise<boolean> {
  // Clean up any existing state to handle multiple calls (e.g., HMR, re-bootstrap)
  releaseSession();
  
  tabId = getTabId();

  if (typeof BroadcastChannel === 'undefined') {
    return Promise.resolve(true);
  }

  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    // If BroadcastChannel exists but cannot be constructed (e.g., due to privacy mode),
    // fall back to allowing this tab to own the session.
    channel = null;
    return Promise.resolve(true);
  }
  channel = ch;
  const chRef = ch!; // Non-null here; TS doesn't narrow across closure

  return new Promise((resolve) => {
    let resolved = false;

    const resolveOnce = (value: boolean): void => {
      if (resolved) return;
      resolved = true;
      if (!value) {
        releaseSession();
      }
      resolve(value);
    };

    const handleMessage = (event: MessageEvent): void => {
      const msg = event.data as SessionMessage;
      if (!msg || typeof msg !== 'object' || !msg.type) return;

      switch (msg.type) {
      case 'WHO_IS_OWNER':
        if (isOwner) {
          chRef.postMessage({ type: 'I_AM_OWNER', tabId, } as SessionMessage);
        }
        break;
      case 'I_AM_OWNER':
      case 'HEARTBEAT':
        if (msg.tabId !== tabId) {
          if (!isOwner) {
            resolveOnce(false);
          } else {
            if (msg.tabId < tabId) {
              isOwner = false;
              stopHeartbeat();
              resolveOnce(false);
            }
          }
        }
        break;
      case 'RELEASED':
        break;
      default:
        break;
      }
    };

    const timeout = setTimeout(() => {
      if (!resolved) {
        isOwner = true;
        chRef.postMessage({ type: 'I_AM_OWNER', tabId, } as SessionMessage);
        startHeartbeat();
        resolveOnce(true);
      }
    }, ACQUIRE_TIMEOUT_MS);

    chRef.onmessage = (e: MessageEvent): void => {
      handleMessage(e);
      const msg = e.data as SessionMessage;
      if ((msg?.type === 'I_AM_OWNER' || msg?.type === 'HEARTBEAT') && msg.tabId !== tabId) {
        clearTimeout(timeout);
      }
    };

    chRef.postMessage({ type: 'WHO_IS_OWNER', } as SessionMessage);
  });
}

/**
 * Register beforeunload to release the session when the tab closes.
 * Must be called after initSingleTabSession resolves with true.
 */
export function registerSessionCleanup(): void {
  window.addEventListener('beforeunload', releaseSession);
  window.addEventListener('pagehide', releaseSession);
}
